<template>
  <template v-if="numberOfPlayers > 1">
    <div class="flex items-center justify-center gap-4 md:gap-24 space-x-8 relative">
      <!-- One block per player -->
      <div v-for="player in opponents" :key="player.id" class="flex flex-col justify-center items-center transition-opacity duration-500" :class="[player.reconnectTimeout !== -1 ? 'opacity-50' : '']">
        <div class="flex flex-col items-center justify-center gap-2 lg:gap-4 relative">
          <BaseAvatar :src="player.avatarUrl" :alt="player.firstName" :isActive="player.status == 'online'" :showRing="Boolean(currentPlayer?.id == player.id)" />
          <span class="text-white font-medium text-center text-base mb-1">{{ player.username }}</span>
          <Transition
            enter-active-class="transition-all duration-300 ease-out"
            enter-from-class="scale-0 opacity-0"
            enter-to-class="scale-100 opacity-100"
            leave-active-class="transition-all duration-200 ease-in"
            leave-from-class="scale-100 opacity-100"
            leave-to-class="scale-0 opacity-0"
          >
            <ActionBubble v-if="showBubble && messagePlayerID == player.id" :message class="absolute top-[25px] right-full translate-y-[-50%] translate-x-[-12px] origin-right" />
          </Transition>
        </div>
        <img
          src="@/assets/images/card-back-stack.png"
          alt=""
          class="heightMd:ml-2 lg:w-18 h-auto md:w-14 heightMd:w-8 heightSm:w-10 transition-all duration-500"
          :class="[player.status === 'offline' || player.status === 'exited' ? 'grayscale' : '']"
        />
      </div>
    </div>
  </template>
  <template v-else>
    <div class="flex items-center justify-center relative">
      <CardBack
        v-for="n in cardsCount"
        :key="n"
        class="mx-0.5 lg:mx-1 h-auto lg:w-22 md:w-14 heightMd:w-12 heightMd:h-15 heightSm:w-10 relative transition-all duration-300 ease-out"
        :class="{ 'opacity-50 grayscale': opponents?.[0].reconnectTimeout !== -1 }"
      />
      <Transition
        enter-active-class="transition-all duration-300 ease-out"
        enter-from-class="scale-0 opacity-0"
        enter-to-class="scale-100 opacity-100"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="scale-100 opacity-100"
        leave-to-class="scale-0 opacity-0"
      >
        <ActionBubble v-if="showBubble" :message class="absolute top-1/2 right-full translate-y-[-50%] origin-right" />
      </Transition>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import BaseAvatar from '@/components/base/BaseAvatar.vue'
import type { PlayerT } from '@/types/User'
import CardBack from '@/assets/svgs/cards/cardback.svg'
import ActionBubble from '@/components/interface/ActionBubble.vue'
import { useHelpers } from '@/composables/useHelpers'

const {
  cardsCount = 7,
  opponents = null,
  currentPlayer = null,
  message = '',
  messagePlayerID = ''
} = defineProps<{
  cardsCount?: number
  opponents?: PlayerT[] | null
  currentPlayer?: PlayerT | null
  message?: string
  messagePlayerID?: string
}>()
const { sleep } = useHelpers()

const showBubble = ref(false)
const timerRef = ref<null | ReturnType<typeof setTimeout>>(null)

const numberOfPlayers = computed(() => {
  return opponents?.length || 1
})

watch(
  () => message,
  async (newValue, oldValue) => {
    if (newValue) {
      if (newValue !== oldValue) {
        showBubble.value = false
        clearTimeout(timerRef.value as ReturnType<typeof setTimeout>)
        await sleep(500)
      }
      showBubble.value = true
      timerRef.value = setTimeout(() => {
        showBubble.value = false
      }, 4000)
    }
  }
)
</script>
